import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
    User as FirebaseUser,
    onAuthStateChanged,
    signInWithPopup,
    getRedirectResult,
    signOut
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { User, UserRole } from '../types';

interface AuthContextType {
    user: User | null;
    firebaseUser: FirebaseUser | null;
    loading: boolean;
    error: string | null;
    signInWithGoogle: () => Promise<void>;
    logout: () => Promise<void>;
    hasPermission: (permission: string) => boolean;
    hasRole: (roles: UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// RBAC Permission Map (shared with Magnus-Core backend)
const PERMISSION_MAP: Record<string, UserRole[]> = {
    // PFMEA Permissions
    'pfmea.view': ['viewer', 'engineer', 'org_admin', 'platform_admin', 'autoGainAdmin'],
    'pfmea.create': ['engineer', 'org_admin', 'platform_admin', 'autoGainAdmin'],
    'pfmea.edit': ['engineer', 'org_admin', 'platform_admin', 'autoGainAdmin'],
    'pfmea.delete': ['engineer', 'org_admin', 'platform_admin', 'autoGainAdmin'],
    'pfmea.approve': ['org_admin', 'platform_admin', 'autoGainAdmin'],

    // Control Plan Permissions
    'control_plan.view': ['viewer', 'engineer', 'org_admin', 'platform_admin', 'autoGainAdmin'],
    'control_plan.create': ['engineer', 'org_admin', 'platform_admin', 'autoGainAdmin'],
    'control_plan.edit': ['engineer', 'org_admin', 'platform_admin', 'autoGainAdmin'],
    'control_plan.approve': ['org_admin', 'platform_admin', 'autoGainAdmin'],

    // Process Flow Permissions
    'process_flow.view': ['viewer', 'engineer', 'org_admin', 'platform_admin', 'autoGainAdmin'],
    'process_flow.create': ['engineer', 'org_admin', 'platform_admin', 'autoGainAdmin'],
    'process_flow.edit': ['engineer', 'org_admin', 'platform_admin', 'autoGainAdmin'],

    // AI Features
    'ai.use_basic': ['viewer', 'engineer', 'org_admin', 'platform_admin', 'autoGainAdmin'],
    'ai.use_prime': ['engineer', 'org_admin', 'platform_admin', 'autoGainAdmin'],
    'ai.generate_pfmea': ['engineer', 'org_admin', 'platform_admin', 'autoGainAdmin'],

    // Work Instructions
    'work_instruction.view': ['viewer', 'engineer', 'org_admin', 'platform_admin', 'autoGainAdmin'],
    'work_instruction.create': ['engineer', 'org_admin', 'platform_admin', 'autoGainAdmin'],
    'work_instruction.approve': ['org_admin', 'platform_admin', 'autoGainAdmin'],
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Check for redirect result on mount (for production auth flow)
    useEffect(() => {
        // Clear any previous errors
        setError(null);

        const handleRedirectResult = async () => {
            try {
                const result = await getRedirectResult(auth);
                if (result) {
                    console.log('✅ Redirect sign-in successful:', result.user.email);
                }
            } catch (err) {
                console.error('Redirect result error:', err);
                const message = err instanceof Error ? err.message : String(err);
                if (message.includes('popup-closed-by-user')) {
                    // Ignore popup errors in redirect flow context
                    setError(null);
                } else {
                    setError(message);
                }
            }
        };
        handleRedirectResult();
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
            setLoading(true);

            if (fbUser) {
                setFirebaseUser(fbUser);

                try {
                    // =========================================================
                    // ISO 27001 CRITICAL SECURITY: Check Firestore approval
                    // =========================================================
                    const { getFirestore, doc, getDoc } = await import('firebase/firestore');
                    const db = getFirestore();
                    const userDocRef = doc(db, 'users', fbUser.uid);
                    const userDoc = await getDoc(userDocRef);

                    // If user document doesn't exist or isApproved is false, deny access
                    if (!userDoc.exists()) {
                        console.warn('🚫 User not found in Firestore. Access denied.');
                        setUser(null);
                        setLoading(false);
                        return;
                    }

                    const userData = userDoc.data();
                    const isApproved = userData?.isApproved === true;
                    const firestoreRole = userData?.role;

                    // Block unapproved users (except autoGainAdmin)
                    if (!isApproved && firestoreRole !== 'autoGainAdmin') {
                        console.warn('🚫 User not approved. Access denied.');
                        setUser({
                            uid: fbUser.uid,
                            email: fbUser.email || '',
                            displayName: fbUser.displayName || 'User',
                            photoURL: fbUser.photoURL || null,
                            role: 'pending' as UserRole,
                            permissions: [],
                            isApproved: false,
                        });
                        setLoading(false);
                        return;
                    }

                    // User is approved - grant access
                    // Get custom claims for role (fallback to Firestore role)
                    const tokenResult = await fbUser.getIdTokenResult();
                    const role = (tokenResult.claims.role as UserRole) || firestoreRole || 'pending';
                    const allowedApps = userData?.allowedApps || ['items']; // defaults

                    // Build permissions list based on role
                    const permissions = Object.entries(PERMISSION_MAP)
                        .filter(([_, roles]) => roles.includes(role))
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        .map(([permission]) => permission);

                    // Add App-Level permissions based on allowedApps
                    if (allowedApps.includes('resolve')) permissions.push('resolve.view');
                    if (allowedApps.includes('dfmea')) permissions.push('dfmea.view');

                    setUser({
                        uid: fbUser.uid,
                        email: fbUser.email || '',
                        displayName: fbUser.displayName || userData?.displayName || 'User',
                        photoURL: fbUser.photoURL || null,
                        role,
                        permissions,
                        allowedApps,
                        organizationId: tokenResult.claims.organizationId as string | undefined,
                        isApproved: true,
                    });
                } catch (error) {
                    console.error('Error checking user approval:', error);
                    setUser(null);
                }
            } else {
                setFirebaseUser(null);
                setUser(null);
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const signInWithGoogle = async () => {
        try {
            setError(null);
            setError(null);
            console.log('🔄 Using popup sign-in for authentication...');

            // Use popup for both dev and prod - more reliable with correct COOP headers
            await signInWithPopup(auth, googleProvider);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : String(err);
            setError(message);
            console.error('Sign in error:', err);
        }
    };

    const logout = async () => {
        try {
            await signOut(auth);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : String(err);
            setError(message);
        }
    };

    const hasPermission = (permission: string): boolean => {
        if (!user) return false;
        return user.permissions.includes(permission);
    };

    const hasRole = (roles: UserRole[]): boolean => {
        if (!user) return false;
        return roles.includes(user.role);
    };

    return (
        <AuthContext.Provider value={{
            user,
            firebaseUser,
            loading,
            error,
            signInWithGoogle,
            logout,
            hasPermission,
            hasRole,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
};
